import { segmentDataStructureTypeToDataStructure } from "@redotech/redo-customer-segments-common/marketing/segments/segment-data-structures";
import { CustomerDataSource } from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import FilterLines from "@redotech/redo-icons/filter-lines.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex, FlexProps } from "@redotech/redo-web/flex";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo, useState } from "react";
import { FullDateTimeframeFilter } from "../data-filters/full-date-timeframe-filter";
import {
  blockBorderProps,
  SegmentBuilderLocationContext,
  SegmentEditorLocation,
} from "../segment-builder-editor";
import { SegmentBuilderText } from "../segment-builder-text";
import { CustomerActivityConditionInProgress } from "../segment-in-progress";
import {
  Activity,
  AnyActivity,
  getActivity,
  isCustomActivity,
} from "./customer-activities";
import { ActivityCountSelector } from "./segment-activity-count-selector";
import { SegmentActivitySelectModal } from "./segment-activity-select-modal";
import {
  generateDefaultWhereCondition,
  SegmentActivityWhereConditions,
} from "./segment-activity-where-condition";

export const SegmentActivityCondition = memo(function SegmentActivityCondition({
  condition,
  setCondition,
  deleteRequested,
  availableDataSourcesForOrders,
  availableDataSourcesForMarketing,
  onCustomEventSelected,
}: {
  condition: CustomerActivityConditionInProgress;
  setCondition: (condition: CustomerActivityConditionInProgress) => void;
  deleteRequested: () => void;
  availableDataSourcesForOrders?: CustomerDataSource[];
  availableDataSourcesForMarketing?: CustomerDataSource[];
  onCustomEventSelected?: () => void;
}) {
  const location = useContext(SegmentBuilderLocationContext);

  const containerProps = useMemo<FlexProps>(() => {
    switch (location) {
      case SegmentEditorLocation.SEGMENT_BUILDER:
        return { ...blockBorderProps, p: "md" };
      case SegmentEditorLocation.AUTOMATIONS_PANEL:
        return {};
      default:
        assertNever(location);
    }
  }, [location]);

  const handleActivitySelected = (activity: AnyActivity) => {
    if (isCustomActivity(activity)) {
      onCustomEventSelected?.();
    } else {
      setCondition({ ...condition, activityType: activity.type });
    }
  };

  return (
    <>
      {condition.activityType ? (
        <SegmentActivityConditionConfig
          availableDataSourcesForMarketing={availableDataSourcesForMarketing}
          availableDataSourcesForOrders={availableDataSourcesForOrders}
          condition={condition}
          deleteRequested={deleteRequested}
          onCustomEventSelected={onCustomEventSelected}
          setCondition={setCondition}
        />
      ) : (
        <Flex {...containerProps} align="center" justify="space-between">
          <SegmentActivityConditionInit
            activitySelected={handleActivitySelected}
            availableDataSourcesForMarketing={availableDataSourcesForMarketing}
            availableDataSourcesForOrders={availableDataSourcesForOrders}
            deleteRequested={deleteRequested}
          />
        </Flex>
      )}
    </>
  );
});

const SegmentActivityConditionInit = memo(
  function SegmentActivityConditionInit({
    activitySelected,
    deleteRequested,
    availableDataSourcesForOrders,
    availableDataSourcesForMarketing,
  }: {
    activitySelected: (activity: AnyActivity) => void;
    deleteRequested: () => void;
    availableDataSourcesForOrders?: CustomerDataSource[];
    availableDataSourcesForMarketing?: CustomerDataSource[];
  }) {
    const [activitySelectorOpen, setActivitySelectorOpen] = useState(true);

    return (
      <>
        <SegmentActivitySelectModal
          activitySelected={(activity) => {
            activitySelected(activity);
            setActivitySelectorOpen(false);
          }}
          availableDataSourcesForMarketing={availableDataSourcesForMarketing}
          availableDataSourcesForOrders={availableDataSourcesForOrders}
          isOpen={activitySelectorOpen}
          onModalCloseRequested={() => setActivitySelectorOpen(false)}
        />
        <Flex align="center" gap="md">
          <SegmentBuilderText>Person has</SegmentBuilderText>
          <RedoButton
            hierarchy="secondary"
            IconTrailing={PlusIcon}
            onClick={() => setActivitySelectorOpen(true)}
            text="Select activity"
          />
        </Flex>
        <Flex gap="xxs">
          <RedoButton
            aria-label="Delete"
            IconLeading={TrashIcon}
            onClick={deleteRequested}
            theme="destructive"
          />
        </Flex>
      </>
    );
  },
);

function customerActivityHasFilterableFields(
  condition: CustomerActivityConditionInProgress,
): boolean {
  if (!condition.activityType) {
    return false;
  }
  const schema =
    segmentDataStructureTypeToDataStructure[condition.activityType];
  if (!schema) {
    return false;
  }
  const fields = Object.entries(schema.fields);
  return fields.length > 0;
}

const SegmentActivityConditionConfig = memo(
  function SegmentActivityConditionConfig({
    deleteRequested,
    condition,
    setCondition,
    availableDataSourcesForOrders,
    availableDataSourcesForMarketing,
    onCustomEventSelected,
  }: {
    deleteRequested: () => void;
    condition: CustomerActivityConditionInProgress;
    setCondition: (condition: CustomerActivityConditionInProgress) => void;
    availableDataSourcesForOrders?: CustomerDataSource[];
    availableDataSourcesForMarketing?: CustomerDataSource[];
    onCustomEventSelected?: () => void;
  }) {
    const showFilters = customerActivityHasFilterableFields(condition);

    const location = useContext(SegmentBuilderLocationContext);

    const containerProps = useMemo<FlexProps>(() => {
      switch (location) {
        case SegmentEditorLocation.SEGMENT_BUILDER:
          return { ...blockBorderProps, p: "md" };
        case SegmentEditorLocation.AUTOMATIONS_PANEL:
          return {};
        default:
          assertNever(location);
      }
    }, [location]);

    return (
      <Flex {...containerProps} dir="row" justify="space-between">
        <Flex dir="column" overflow="hidden">
          <Flex align="center" gap="md" wrap="wrap">
            <SegmentBuilderText>Person has</SegmentBuilderText>
            <ActivitySelector
              availableDataSourcesForMarketing={
                availableDataSourcesForMarketing
              }
              availableDataSourcesForOrders={availableDataSourcesForOrders}
              selectedActivity={getActivity(condition.activityType!)} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              setSelectedActivity={(activity) => {
                if (isCustomActivity(activity)) {
                  onCustomEventSelected?.();
                } else {
                  setCondition({
                    ...condition,
                    activityType: activity.type,
                    whereConditions: [],
                  });
                }
              }}
            />
            <ActivityCountSelector
              activityCount={condition.count}
              setActivityCount={(count) => {
                setCondition({ ...condition, count });
              }}
            />
            <FullDateTimeframeFilter
              comparison={condition.timeframe}
              setComparison={(timeframe) => {
                setCondition({ ...condition, timeframe });
              }}
            />
            {showFilters && (
              <RedoButton
                aria-label="Add filter"
                IconLeading={FilterLines}
                onClick={() => {
                  const newCondition = generateDefaultWhereCondition(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    condition.activityType!,
                  );
                  if (!newCondition) {
                    return;
                  }
                  const newWhereConditions = [
                    ...(condition.whereConditions || []),
                    newCondition,
                  ];
                  setCondition({
                    ...condition,
                    whereConditions: newWhereConditions,
                  });
                }}
              />
            )}
          </Flex>
          <SegmentActivityWhereConditions
            activityType={condition.activityType!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
            availableDataSourcesForMarketing={availableDataSourcesForMarketing}
            availableDataSourcesForOrders={availableDataSourcesForOrders}
            setWhereConditions={(whereConditions) => {
              setCondition({ ...condition, whereConditions });
            }}
            whereConditions={condition.whereConditions}
          />
        </Flex>
        <Flex gap="xxs">
          <RedoButton
            aria-label="Delete"
            IconLeading={TrashIcon}
            onClick={deleteRequested}
            theme="destructive"
          />
        </Flex>
      </Flex>
    );
  },
);

const ActivitySelector = memo(function ActivitySelector({
  selectedActivity,
  setSelectedActivity,
  availableDataSourcesForOrders,
  availableDataSourcesForMarketing,
}: {
  selectedActivity: Activity | undefined;
  setSelectedActivity: (activity: AnyActivity) => void;
  availableDataSourcesForOrders?: CustomerDataSource[];
  availableDataSourcesForMarketing?: CustomerDataSource[];
}) {
  const [activitySelectorOpen, setActivitySelectorOpen] = useState(false);

  return (
    <>
      <SegmentActivitySelectModal
        activitySelected={(activity) => {
          setSelectedActivity(activity);
          setActivitySelectorOpen(false);
        }}
        availableDataSourcesForMarketing={availableDataSourcesForMarketing}
        availableDataSourcesForOrders={availableDataSourcesForOrders}
        initialSelectedActivity={selectedActivity}
        isOpen={activitySelectorOpen}
        onModalCloseRequested={() => setActivitySelectorOpen(false)}
      />
      <Flex>
        <RedoSingleSelectDropdownInputButton<Activity>
          isDropdownOpen={false}
          onClick={() => setActivitySelectorOpen(true)}
          selectedItem={
            selectedActivity
              ? {
                  id: selectedActivity.name,
                  type: "text",
                  text: selectedActivity.name,
                  value: selectedActivity,
                }
              : undefined
          }
          size="xs"
        />
      </Flex>
    </>
  );
});
